import { useId } from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { PayoutStatementView } from '@/api/generated';
import { ChartDataTable } from '@/components/ChartDataTable';
import { moneyIn } from '@/components/moneyIn';
import { formatCivilOrDash } from '@/i18n/format';
import { useCivilRange } from '@/i18n/useCivilRange';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme';
import { payableOf } from './payoutStatus';
import { lastTwelve } from './statementMath';

export interface PayableBarsProps {
  statements: PayoutStatementView[];
  currency: string;
}

export function PayableBars({ statements, currency }: PayableBarsProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const bars = lastTwelve(statements);
  const max = bars.reduce((acc, stmt) => Math.max(acc, payableOf(stmt)), 0);
  const dash = t('statements.unavailable');
  const civilRange = useCivilRange(dash);
  const dataId = useId();
  const title = t('statements.barsTitle', { currency: currency || dash });
  const shown = bars.map((stmt) => {
    const payableMinor = payableOf(stmt);
    return {
      key: String(stmt.id),
      payableMinor,
      payable: moneyIn(payableMinor, currency, locale, dash),
      period: civilRange(stmt.periodStart, stmt.periodEnd),
      label: formatCivilOrDash(stmt.periodEnd, locale, dash, { month: 'short', day: 'numeric' }),
    };
  });

  return (
    <Paper sx={{ p: 2.5, minWidth: 0, height: '100%' }}>
      <Typography variant="subtitle1" component="h2">
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: tokens.text2, mb: 2 }}>
        {t('statements.barsNote')}
      </Typography>
      {bars.length === 0 ? (
        <Typography variant="body2" sx={{ color: tokens.text2 }}>
          {t('payouts.empty')}
        </Typography>
      ) : (
        <>
          <Box
            role="img"
            aria-label={t('statements.barsSummary', { count: bars.length })}
            aria-describedby={dataId}
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              height: 140,
              borderBottom: '1px solid',
              borderColor: tokens.line,
            }}
          >
            {shown.map((bar) => {
              const pct = max > 0 ? (bar.payableMinor / max) * 100 : 0;
              return (
                <Tooltip
                  describeChild
                  key={bar.key}
                  title={t('statements.barTip', { period: bar.period, payable: bar.payable })}
                >
                  <Box
                    sx={{
                      flex: 1,
                      height: `${pct}%`,
                      bgcolor: tokens.brand,
                      minHeight: pct > 0 ? 4 : 0,
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
            {shown.map((bar) => (
              <Typography
                key={`lbl-${bar.key}`}
                variant="caption"
                sx={{ flex: 1, textAlign: 'center', color: tokens.text2 }}
              >
                {bar.label}
              </Typography>
            ))}
          </Box>
          <ChartDataTable
            id={dataId}
            label={title}
            columns={[t('statements.barsPeriod'), t('statements.barsPayable')]}
            rows={shown.map((bar) => ({ key: bar.key, label: bar.period, values: [bar.payable] }))}
          />
        </>
      )}
    </Paper>
  );
}
